import { fitnessQuestionnaireSchema } from '../../validation/fitness-questionnaire.schemas';
import {
  EQUIPMENT_ACCESS_OPTIONS,
  FITNESS_GOAL_OPTIONS,
  FITNESS_LEVEL_OPTIONS,
  GOAL_TIMELINE_OPTIONS,
  getLabelForValue,
  TIME_COMMITMENT_OPTIONS,
  WORKOUT_FREQUENCY_OPTIONS,
  WORKOUT_LOCATION_OPTIONS,
} from '../select-options';

import type { QuestionnaireProgressStepSlug } from './questionnaire-progress';
import type { FitnessLevel, WorkoutFrequency } from '../../types';
import type {
  PlanReview,
  PlanReviewStatus,
} from '../../types/domain/plan.types';
import type {
  FitnessQuestionnaireInput,
  FitnessQuestionnaireOutput,
  signUpQuestionnaireSchema,
} from '../../validation/fitness-questionnaire.schemas';

/**
 * Fitness Questionnaire Form Model
 *
 * What the questionnaire and the refine-plan form share that is not drawing:
 * the steps and their copy, the starting values, the saved answers mapped back
 * onto the form, validation by the shared schema, and the small conversions
 * the fields need. Used by the web page and modal and by the mobile screens, so
 * both ask the same questions in the same words and judge the answers the same
 * way.
 */

// =============================================================================
// STEPS
// =============================================================================

/** A field of the questionnaire. */
export type QuestionnaireField = keyof FitnessQuestionnaireInput;

/** One step of the flow, without the component that draws it. */
export interface QuestionnaireStepMeta {
  /** Stable identifier, and the step's URL slug on web. */
  id: QuestionnaireProgressStepSlug;
  /** Short label shown next to the progress bar. */
  label: string;
  /** Step heading. */
  title: string;
  /** Supporting sub-heading. */
  subtitle: string;
  /** Fields validated before this step may be left. Empty = no gate. */
  fields: QuestionnaireField[];
}

/**
 * The questionnaire's steps, in order — the source of truth for navigation and
 * progress. Each platform pairs a step with the component that draws it.
 */
export const QUESTIONNAIRE_STEPS: readonly QuestionnaireStepMeta[] = [
  {
    id: 'goals',
    label: 'Goals',
    title: "Let's start with your goals",
    subtitle: 'What you want to achieve — and how soon.',
    fields: ['primary_fitness_goals', 'goal_timeline'],
  },
  {
    id: 'level',
    label: 'Starting point',
    title: 'Where are you starting from?',
    subtitle: 'This helps us pitch your plan at the right intensity.',
    fields: ['current_fitness_level'],
  },
  {
    id: 'setup',
    label: 'Your setup',
    title: 'How do you like to train?',
    subtitle: 'Where, with what, how often and for how long.',
    // Optional to the API; the sign-up schema requires them.
    fields: [
      'primary_workout_location',
      'equipment_on_hand',
      'current_days_of_exercise',
      'minutes_of_workout',
    ],
  },
  {
    id: 'watch-outs',
    label: 'Watch-outs',
    title: 'Anything we should work around?',
    subtitle:
      'Tell us about any injuries or limitations so we can train safely.',
    fields: ['has_limitations', 'limitation_description'],
  },
  {
    id: 'generate',
    label: 'Generate',
    title: 'Build my plan',
    subtitle:
      "We'll add a new plan built from your goals — your existing plans stay.",
    fields: [],
  },
];

/** The index of a step by its id, or -1 for an id that is not a step. */
export function getQuestionnaireStepIndex(id: string | undefined): number {
  return QUESTIONNAIRE_STEPS.findIndex((step) => step.id === id);
}

// =============================================================================
// VALUES
// =============================================================================

/** The form's values while it is being filled in: every field optional. */
export type QuestionnaireFormValues = Partial<FitnessQuestionnaireInput>;

/** The longest a limitation's description may be — the schema's own ceiling. */
export const MAX_LIMITATION_LENGTH = 500;

/** The youngest age the questionnaire takes. */
export const MIN_QUESTIONNAIRE_AGE = 16;

/** The bounds of the 1–10 "how much does this matter" rating. */
export const GOAL_PRIORITY_RANGE = { min: 1, max: 10 } as const;

/**
 * What a blank questionnaire starts with, so every control is controlled from
 * the first render. Required choices stay unset until made.
 */
export function createQuestionnaireDefaults(): QuestionnaireFormValues {
  return {
    primary_fitness_goals: [],
    has_limitations: false,
    generate_personalized_plan: true,
  };
}

/** Make every field present and nullable (the optional `?` modifier removed). */
type Nullable<T> = { [K in keyof T]-?: NonNullable<T[K]> | null };

/**
 * The `GET /fitness-questionnaire?template=true` payload — the user's saved
 * answers in the questionnaire's own shape. It mirrors the form input (minus the
 * per-submit `generate_personalized_plan`) but returns `null` for every
 * unanswered field, plus the record `id` and the review of these answers.
 */
export type SavedQuestionnaireAnswers = Nullable<
  Omit<FitnessQuestionnaireInput, 'generate_personalized_plan'>
> & { id?: string; plan_review?: PlanReview | null };

const nullToUndef = <T>(value: T | null): T | undefined =>
  value == null ? undefined : value;

/**
 * The saved answers as form values. The GET returns `null` for unanswered
 * fields; the schema's optional fields want `undefined`. No saved answers is a
 * blank form.
 */
export function savedAnswersToFormValues(
  answers: SavedQuestionnaireAnswers | null
): QuestionnaireFormValues {
  if (!answers) return createQuestionnaireDefaults();
  return {
    primary_fitness_goals: answers.primary_fitness_goals ?? [],
    goal_timeline: nullToUndef(answers.goal_timeline),
    current_fitness_level: nullToUndef(answers.current_fitness_level),
    has_limitations: answers.has_limitations ?? false,
    generate_personalized_plan: true,
    goal_priority: nullToUndef(answers.goal_priority),
    limitation_description: nullToUndef(answers.limitation_description),
    age: nullToUndef(answers.age),
    gender: nullToUndef(answers.gender),
    height: nullToUndef(answers.height),
    weight: nullToUndef(answers.weight),
    current_days_of_exercise: nullToUndef(answers.current_days_of_exercise),
    minutes_of_workout: nullToUndef(answers.minutes_of_workout),
    favourite_workouts: nullToUndef(answers.favourite_workouts),
    primary_workout_location: nullToUndef(answers.primary_workout_location),
    equipment_on_hand: nullToUndef(answers.equipment_on_hand),
    motivation: nullToUndef(answers.motivation),
    motivation_other: nullToUndef(answers.motivation_other),
    biggest_challenge: nullToUndef(answers.biggest_challenge),
    biggest_challenge_other: nullToUndef(answers.biggest_challenge_other),
  };
}

/**
 * Where the sign-up questionnaire starts: the saved answers, with a local
 * draft on top for the fields it holds. Sign-up sends the whole form, and the
 * API saves a left-out field as `null` — so the fields its steps never show
 * have to start from what is saved, or a second sign-up would clear them.
 */
export function seedSignUpValues(
  saved: SavedQuestionnaireAnswers | null,
  draft: QuestionnaireFormValues | null
): QuestionnaireFormValues {
  const values: Record<string, unknown> = {
    ...savedAnswersToFormValues(saved),
  };
  for (const [field, value] of Object.entries(draft ?? {})) {
    if (value !== undefined) values[field] = value;
  }
  // The Watch-outs switch clears the description when it goes off; a draft
  // that says "no limitations" does the same to a saved one.
  if (values.has_limitations !== true) delete values.limitation_description;
  return values as QuestionnaireFormValues;
}

// =============================================================================
// VALIDATION
// =============================================================================

/** The first message against each field that has one. */
export type QuestionnaireErrors = Partial<Record<QuestionnaireField, string>>;

/**
 * The rules the answers are judged by: the API's (`fitnessQuestionnaireSchema`,
 * what Adjust goals uses), or sign-up's, which also requires the plan inputs.
 */
export type QuestionnaireSchema =
  typeof fitnessQuestionnaireSchema | typeof signUpQuestionnaireSchema;

/**
 * Judge the answers by the shared schema. With `fields`, only those are
 * reported — a step's gate; without, the whole form. An empty result means
 * there is nothing wrong with what was asked about.
 */
export function getQuestionnaireErrors(
  values: QuestionnaireFormValues,
  fields?: readonly QuestionnaireField[],
  schema: QuestionnaireSchema = fitnessQuestionnaireSchema
): QuestionnaireErrors {
  const result = schema.safeParse(values);
  if (result.success) return {};

  const errors: QuestionnaireErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as QuestionnaireField | undefined;
    if (!field) continue;
    if (fields && !fields.includes(field)) continue;
    errors[field] ??= issue.message;
  }
  return errors;
}

/** The answers ready to send, or null when the schema refuses them. */
export function parseQuestionnaire(
  values: QuestionnaireFormValues,
  schema: QuestionnaireSchema = fitnessQuestionnaireSchema
): FitnessQuestionnaireOutput | null {
  const result = schema.safeParse(values);
  return result.success ? result.data : null;
}

// =============================================================================
// "OTHER" ENTRIES
// =============================================================================

/**
 * Backend `EnumOrOther` convention: a custom entry is submitted as
 * `other:<text>` (a bare `other` is rejected). Free-text entries are stored
 * with this prefix and shown without it.
 */
export const OTHER_PREFIX = 'other:';

/** A typed entry as it is stored, or null when there is nothing to store. */
export function toOtherEntry(text: string): string | null {
  const trimmed = text.trim();
  return trimmed ? `${OTHER_PREFIX}${trimmed}` : null;
}

/** A stored value as it is shown: a custom entry loses its prefix. */
export function stripOtherPrefix(value: string): string {
  return value.startsWith(OTHER_PREFIX)
    ? value.slice(OTHER_PREFIX.length)
    : value;
}

// =============================================================================
// COPY
// =============================================================================

/** Plain-language sub-copy for each fitness level (the choice cards' descriptions). */
export const LEVEL_DESCRIPTIONS: Record<FitnessLevel, string> = {
  beginner: 'New to training, or coming back after a long break.',
  intermediate: 'You train semi-regularly and know the basic movements.',
  advanced: 'You train consistently with solid technique and structure.',
  athlete: 'You compete or train at a high level year-round.',
};

/** Short sub-copy for the workout-location choice cards. */
export const LOCATION_DESCRIPTIONS: Record<string, string> = {
  home: 'Bodyweight and whatever kit you keep at home.',
  gym: 'Full access to machines and free weights.',
  outdoors: 'Parks, trails, and open-air sessions.',
  mixed: 'A bit of everything, depending on the day.',
};

/**
 * Where the setup step is drawn: the sign-up flow, or Refine your plan (which
 * reuses it inside "Your routine").
 */
export type SetupStepContext = 'sign-up' | 'refine';

/**
 * The frequency question. The generator reads the answer as the plan's
 * workouts per week, so sign-up asks what the user can fit in; Refine keeps
 * asking what they do now.
 */
export const SETUP_FREQUENCY_LABEL: Record<SetupStepContext, string> = {
  'sign-up': 'How many days a week can you train?',
  refine: 'How many days a week do you train now?',
};

/**
 * The frequency pills. Sign-up drops "None": the generator reads the answer as
 * workouts per week, and a plan of none is no plan (the sign-up schema refuses
 * a saved `'0'` for the same reason). Refine still offers it, as a fact about
 * what the user does now.
 */
export function getSetupFrequencyOptions(
  context: SetupStepContext
): readonly { value: WorkoutFrequency; label: string }[] {
  return context === 'sign-up'
    ? WORKOUT_FREQUENCY_OPTIONS.filter((option) => option.value !== '0')
    : WORKOUT_FREQUENCY_OPTIONS;
}

/**
 * Shown when the saved answers fail to load. The transport's own message
 * ("Network error: …") is for logs, not the user.
 */
export const SAVED_ANSWERS_LOAD_ERROR =
  "We couldn't load your saved answers. Check your connection and try again.";

/**
 * The equipment field's help text. At sign-up a gym user may leave it empty
 * (see `signUpQuestionnaireSchema`), so the text says so once they pick gym.
 */
export function getEquipmentHelpText(
  context: SetupStepContext,
  location: string | undefined
): string {
  return context === 'sign-up' && location === 'gym'
    ? 'Optional at the gym: your plan assumes a full gym.'
    : "Add anything we don't list.";
}

// =============================================================================
// THE RECAP
// =============================================================================

/** One line of the Generate step's recap. */
export interface QuestionnaireRecapRow {
  label: string;
  value: string;
}

const UNSET = '—';

/** The answers so far, said back before the plan is generated. */
export function buildQuestionnaireRecap(
  values: QuestionnaireFormValues
): QuestionnaireRecapRow[] {
  const goals = (values.primary_fitness_goals ?? []).map(
    (goal) =>
      getLabelForValue(FITNESS_GOAL_OPTIONS, goal) ?? stripOtherPrefix(goal)
  );
  const equipment = (values.equipment_on_hand ?? []).map(
    (item) =>
      getLabelForValue(EQUIPMENT_ACCESS_OPTIONS, item) ?? stripOtherPrefix(item)
  );
  return [
    { label: 'Goals', value: goals.join(', ') || UNSET },
    {
      label: 'Timeline',
      value:
        (values.goal_timeline &&
          getLabelForValue(GOAL_TIMELINE_OPTIONS, values.goal_timeline)) ||
        UNSET,
    },
    {
      label: 'Starting point',
      value:
        (values.current_fitness_level &&
          getLabelForValue(
            FITNESS_LEVEL_OPTIONS,
            values.current_fitness_level
          )) ||
        UNSET,
    },
    {
      label: 'Where',
      value:
        (values.primary_workout_location &&
          getLabelForValue(
            WORKOUT_LOCATION_OPTIONS,
            values.primary_workout_location
          )) ||
        UNSET,
    },
    { label: 'Equipment', value: equipment.join(', ') || UNSET },
    {
      label: 'Days a week',
      value:
        (values.current_days_of_exercise &&
          getLabelForValue(
            WORKOUT_FREQUENCY_OPTIONS,
            values.current_days_of_exercise
          )) ||
        UNSET,
    },
    {
      label: 'Session length',
      value:
        (values.minutes_of_workout &&
          getLabelForValue(
            TIME_COMMITMENT_OPTIONS,
            values.minutes_of_workout
          )) ||
        UNSET,
    },
    {
      label: 'Limitations',
      value: needsPlanReview(values) ? PLAN_REVIEW_COPY.recapValue : 'None',
    },
  ];
}

// =============================================================================
// PLAN REVIEW
// =============================================================================

/**
 * A questionnaire that declares an injury never gets a plan automatically. An
 * admin reviews the injury, then either approves a plan built around it or
 * declines and tells the user to consult a professional. So the questionnaire
 * says so the moment the injury is declared, and what follows sending is
 * "being reviewed" rather than a progress bar.
 *
 * This reads the answers, so it is for what is said *before* sending — the
 * notice, the button. After sending, the API says what happened: the job ends
 * `held`, and `plan_review` on the session user says where the review stands.
 */
export function needsPlanReview(values: QuestionnaireFormValues): boolean {
  return values.has_limitations === true;
}

/**
 * The words the review is explained in — one set for both platforms. Nothing
 * here promises a plan: a review can end in a decline.
 */
export const PLAN_REVIEW_COPY = {
  /** The notice that arrives with the injury switch. */
  noticeTitle: "We'll review this before building your plan",
  noticeBody:
    "Plans around an injury aren't generated automatically. One of our team looks at what you tell us here first, then we'll let you know the outcome.",
  /** The description field, asked as the real question. */
  descriptionLabel: 'What should we work around?',
  descriptionHelp:
    'Which area, and what makes it worse. This is what the review looks at.',
  /** The recap's Limitations row. */
  recapValue: 'Yes · reviewed first',
  /** The last step's note and button. */
  generateNote:
    "One of our team will review your injury before a plan is built, and we'll let you know the outcome. Your existing plans stay.",
  generateLabel: 'Send for review',
  /** Refine your plan's primary button and its confirmations. */
  refineLabel: 'Save & send for review',
  refineToast: "Sent for review — we'll let you know the outcome.",
  /** Sent again while a review was already pending: it now covers these. */
  refineUpdatedToast: "We've updated your review with your new answers.",
  /** The screen that follows sending, and the dashboard's pending card. */
  screenTitle: 'Your plan is being reviewed',
  screenBody:
    "We have your answers. One of our team is reviewing the injury you told us about before a plan is built. There's nothing more you need to do.",
  screenAction: 'Go to dashboard',
  pendingCardBody:
    "One of our team is reviewing the injury you told us about. We'll let you know the outcome.",
  /** The dashboard's card after a decline. The words are the API's own. */
  declinedTitle: 'Consult a professional first',
  declinedBody:
    "We can't safely build a plan around the limitation you told us about. Please consult a fitness or medical professional before you start training.",
} as const;

/** What the last step says under the recap. */
export function getGenerateNote(values: QuestionnaireFormValues): string {
  return needsPlanReview(values)
    ? PLAN_REVIEW_COPY.generateNote
    : "We'll add a new plan built from your current goals. Your existing plans stay.";
}

/** The last step's button: it says what pressing it does. */
export function getGenerateLabel(values: QuestionnaireFormValues): string {
  return needsPlanReview(values)
    ? PLAN_REVIEW_COPY.generateLabel
    : 'Generate my plan';
}

/**
 * What Refine your plan says once it has saved. Sending for review again
 * while one is pending opens no second review — the same one now covers the
 * new answers — so it says that instead.
 */
export function getRefineSavedMessage(
  values: QuestionnaireFormValues,
  generate: boolean,
  currentReview: PlanReviewStatus | null | undefined
): string {
  if (needsPlanReview(values)) {
    if (currentReview === 'pending') return PLAN_REVIEW_COPY.refineUpdatedToast;
    if (generate) return PLAN_REVIEW_COPY.refineToast;
  }
  return generate
    ? 'Building your plan — track it in the bell.'
    : 'Saved — your goals are updated.';
}

/** The dashboard card a review puts in place of the questionnaire banner. */
export type PlanReviewCard = 'reviewing' | 'declined';

/**
 * Which review card the dashboard shows, if any. The "get a plan built around
 * you" banner must not show while a review is pending or declined — one would
 * invite the user to start again, the other to ignore what they were told.
 *
 * - `pending`  → `reviewing`, whatever plans the user has: something is on
 *   its way and this says so.
 * - `declined` → `declined`, but only for a user with no active plan. It stands
 *   where the banner would; someone already training does not need it pinned
 *   to their dashboard.
 * - `approved`, or no review → `null`: the usual banner rule applies.
 */
export function resolvePlanReviewCard(
  status: PlanReviewStatus | null | undefined,
  hasActivePlan: boolean
): PlanReviewCard | null {
  if (status === 'pending') return 'reviewing';
  if (status === 'declined' && !hasActivePlan) return 'declined';
  return null;
}

/**
 * A value that changes whenever something says the user's review has moved: a
 * `plan_review_*` notification arriving, or a generation job ending held. The
 * session user carries `plan_review`, and is otherwise read only at sign-in —
 * so each platform refetches it when this changes.
 */
export function getPlanReviewSignal(
  notifications: readonly { id: string; data: unknown }[],
  jobs: Readonly<
    Record<string, { job_id: string; status: string; metadata?: unknown }>
  >
): string {
  const kinds = notifications
    .filter((notification) => {
      const kind = (notification.data as { kind?: unknown } | null)?.kind;
      return typeof kind === 'string' && kind.startsWith('plan_review_');
    })
    .map((notification) => notification.id);
  const held = Object.values(jobs)
    .filter((job) => job.status === 'held')
    .map((job) => job.job_id);
  return [...kinds, ...held].sort().join('|');
}

/** One line of the review screen's "where is it" list. */
export interface PlanReviewStep {
  id: 'received' | 'reviewing' | 'outcome';
  label: string;
  state: 'done' | 'current' | 'upcoming';
  /** A line under the label. */
  detail?: string;
  /** The user's own description, said back under the current step. */
  quote?: string;
}

/**
 * Where a review stands, as three steps. The user's description of the injury
 * is quoted under the review, so they can see what is being looked at. The
 * last step is an outcome, not a plan: the review may decline.
 */
export function buildPlanReviewSteps(
  values: QuestionnaireFormValues
): PlanReviewStep[] {
  const description = values.limitation_description?.trim();
  return [
    { id: 'received', label: 'Answers received', state: 'done' },
    {
      id: 'reviewing',
      label: 'Injury under review',
      state: 'current',
      ...(description
        ? { detail: 'What you told us:', quote: description }
        : {}),
    },
    {
      id: 'outcome',
      label: "We'll let you know",
      state: 'upcoming',
      detail: "You'll get a notification either way.",
    },
  ];
}

// =============================================================================
// NUMBER FIELDS
// =============================================================================

/** A number field's text as a number, or `undefined` when cleared or not one. */
export function parseNumberInput(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

/** Decimal feet → { ft, inches } (12-inch rollover guarded). */
export function feetToFtIn(decimalFeet: number): {
  ft: number;
  inches: number;
} {
  let ft = Math.floor(decimalFeet);
  let inches = Math.round((decimalFeet - ft) * 12);
  if (inches === 12) {
    ft += 1;
    inches = 0;
  }
  return { ft, inches };
}

/** Feet and inches as the decimal feet the backend round-trips. */
export function ftInToDecimalFeet(feet: number, inches: number): number {
  return feet + inches / 12;
}

/**
 * The height an imperial feet/inches pair stands for: decimal feet, or
 * `undefined` when both are empty — a cleared height, not a height of zero.
 */
export function composeImperialHeight(
  feet: number,
  inches: number
): number | undefined {
  return feet === 0 && inches === 0
    ? undefined
    : ftInToDecimalFeet(feet, inches);
}
